/**
 * Simple EventEmitter for browser environments
 * Provides event handling capabilities for SDK classes
 */
export class EventEmitter {
  constructor() {
    this._events = {};
  }

  /**
   * Register an event listener
   * @param {string} event - Event name
   * @param {Function} listener - Callback function
   * @returns {EventEmitter} this for chaining
   */
  on(event, listener) {
    if (!this._events[event]) {
      this._events[event] = [];
    }
    this._events[event].push(listener);
    return this;
  }

  /**
   * Unregister an event listener
   * @param {string} event - Event name
   * @param {Function} listener - Callback function to remove
   * @returns {EventEmitter} this for chaining
   */
  off(event, listener) {
    if (!this._events[event]) {
      return this;
    }
    this._events[event] = this._events[event].filter(l => l !== listener);
    return this;
  }

  /**
   * Emit an event
   * @param {string} event - Event name
   * @param {...any} args - Arguments to pass to listeners
   */
  emit(event, ...args) {
    if (!this._events[event]) {
      return;
    }
    this._events[event].forEach(listener => {
      try {
        listener(...args);
      } catch (error) {
        console.error(`[EventEmitter] Error in ${event} listener:`, error);
      }
    });
  }

  /**
   * Register a one-time event listener
   * @param {string} event - Event name
   * @param {Function} listener - Callback function
   * @returns {EventEmitter} this for chaining
   */
  once(event, listener) {
    const onceWrapper = (...args) => {
      listener(...args);
      this.off(event, onceWrapper);
    };
    this.on(event, onceWrapper);
    return this;
  }

  /**
   * Remove all listeners for an event or all events
   * @param {string} [event] - Event name (optional, removes all if not specified)
   */
  removeAllListeners(event) {
    if (event) {
      delete this._events[event];
    } else {
      this._events = {};
    }
  }
}
